import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 56. CONSENTEMENTS
// Gestion CENTRALISÉE des consentements. Un utilisateur ne doit JAMAIS être
// considéré comme automatiquement consentant à toutes les communications.
// Exemples de finalités : marketing · notifications · communications ·
// traitement de certaines données · partage avec certains services.
// ─────────────────────────────────────────────────────────────

export interface ConsentPurpose {
  key: string;
  label: string;
  desc: string;
  icon: string;
  /** Consentement requis par défaut (opt-in) ou non. */
  defaultOptIn: boolean;
  /** Le canal est-il pertinent pour cette finalité ? */
  channels: string[];
}

export const CONSENT_PURPOSES: ConsentPurpose[] = [
  { key: 'marketing', label: 'Marketing', desc: 'Emails et offres promotionnelles.', icon: 'ri-megaphone-line', defaultOptIn: false, channels: ['email', 'sms', 'push'] },
  { key: 'notifications', label: 'Notifications', desc: 'Alertes et rappels de votre activité.', icon: 'ri-notification-3-line', defaultOptIn: true, channels: ['push', 'email'] },
  { key: 'communications', label: 'Communications', desc: 'Messages opérationnels et service client.', icon: 'ri-chat-3-line', defaultOptIn: true, channels: ['email', 'sms', 'whatsapp'] },
  { key: 'data_processing', label: 'Traitement des données', desc: 'Analyse nécessaire au fonctionnement du service.', icon: 'ri-database-2-line', defaultOptIn: true, channels: ['app'] },
  { key: 'service_sharing', label: 'Partage avec des services tiers', desc: 'Partage avec des partenaires lorsque nécessaire.', icon: 'ri-share-forward-line', defaultOptIn: false, channels: ['app'] },
];

export function consentPurposeMeta(key: string | null | undefined) {
  return CONSENT_PURPOSES.find((p) => p.key === key) || CONSENT_PURPOSES[0];
}

export const CONSENT_CHANNELS: { key: string; label: string; icon: string }[] = [
  { key: 'email', label: 'Email', icon: 'ri-mail-line' },
  { key: 'sms', label: 'SMS', icon: 'ri-message-2-line' },
  { key: 'push', label: 'Push', icon: 'ri-notification-badge-line' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line' },
  { key: 'app', label: 'Application', icon: 'ri-smartphone-line' },
];

export interface CoreConsent {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  user_id: number | null;
  subject_type: string | null;
  subject_id: string | null;
  purpose: string;
  channel: string | null;
  granted: boolean | null;
  source: string | null;
  granted_at: string | null;
  revoked_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export async function listConsents(userId: number): Promise<CoreConsent[]> {
  const { data, error } = await supabase
    .from('core_consents')
    .select('*')
    .eq('user_id', userId)
    .order('purpose', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreConsent[];
}

/** Table { finalité: accordé } pour un utilisateur (utile aux modules). */
export async function getConsentMap(userId: number): Promise<Record<string, boolean>> {
  const rows = await listConsents(userId);
  const map: Record<string, boolean> = {};
  // Une finalité sans ligne explicite n'est PAS consentie par défaut (sauf opt-in).
  CONSENT_PURPOSES.forEach((p) => { map[p.key] = !p.defaultOptIn ? false : p.defaultOptIn; });
  rows.forEach((r) => { map[r.purpose] = !!r.granted; });
  return map;
}

/**
 * Vérifie qu'un consentement est accordé pour une finalité.
 * Les modules appellent cette fonction AVANT d'envoyer une communication.
 */
export async function isConsented(userId: number, purpose: string): Promise<boolean> {
  const map = await getConsentMap(userId);
  return !!map[purpose];
}

export interface SetConsentInput {
  tenantId?: number | null;
  organizationId?: number | null;
  userId: number;
  purpose: string;
  granted: boolean;
  channel?: string | null;
  source?: string | null;
  subjectType?: string;
  subjectId?: string | null;
}

/** Enregistre (ou met à jour) un consentement et journalise le changement. */
export async function setConsent(input: SetConsentInput): Promise<void> {
  const now = new Date().toISOString();
  const existing = await supabase
    .from('core_consents')
    .select('id')
    .eq('user_id', input.userId)
    .eq('purpose', input.purpose)
    .maybeSingle();

  const payload = {
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId ?? null,
    user_id: input.userId,
    subject_type: input.subjectType || 'user',
    subject_id: input.subjectId ?? String(input.userId),
    purpose: input.purpose,
    channel: input.channel || null,
    granted: input.granted,
    source: input.source || 'dashboard',
    granted_at: input.granted ? now : null,
    revoked_at: input.granted ? null : now,
    updated_at: now,
  };

  let consentId = existing.data?.id as number | undefined;
  if (consentId) {
    const { error } = await supabase.from('core_consents').update(payload).eq('id', consentId);
    if (error) throw error;
  } else {
    const { data, error } = await supabase.from('core_consents').insert(payload).select('id').maybeSingle();
    if (error) throw error;
    consentId = (data?.id as number) ?? undefined;
  }

  // Journal du consentement (traçabilité / preuve).
  try {
    await supabase.from('core_consent_log').insert({
      consent_id: consentId ?? null,
      organization_id: input.organizationId ?? null,
      user_id: input.userId,
      purpose: input.purpose,
      action: input.granted ? 'granted' : 'revoked',
      channel: input.channel || null,
      source: input.source || 'dashboard',
    });
  } catch {
    /* la journalisation ne bloque pas */
  }
}

export interface CoreConsentLog {
  id: number;
  consent_id: number | null;
  organization_id: number | null;
  user_id: number | null;
  purpose: string | null;
  action: string | null;
  channel: string | null;
  source: string | null;
  created_at: string | null;
}

export async function listConsentLog(userId: number, limit = 50): Promise<CoreConsentLog[]> {
  const { data, error } = await supabase
    .from('core_consent_log')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as CoreConsentLog[];
}

export interface ConsentStats {
  granted: number;
  revoked: number;
  total: number;
  byPurpose: Record<string, boolean>;
}

export async function fetchConsentStats(userId: number): Promise<ConsentStats> {
  const map = await getConsentMap(userId);
  const byPurpose = map;
  const granted = Object.values(map).filter(Boolean).length;
  return { granted, revoked: Object.keys(map).length - granted, total: Object.keys(map).length, byPurpose };
}

// ─────────────────────────────────────────────────────────────
// Filtrage RÉEL des communications sortantes (spec 56 → 57)
// Avant d'envoyer un email / SMS / Push / WhatsApp, les modules consultent
// le consentement de la personne. L'in-app n'est jamais bloqué.
// ─────────────────────────────────────────────────────────────

/** Un canal est « sortant » s'il quitte l'application (donc soumis au consentement). */
export function isOutboundChannel(channel: string): boolean {
  return channel !== 'in_app' && channel !== 'app';
}

/** Finalité qui gouverne par défaut une communication commerciale. */
export const MARKETING_PURPOSE = 'marketing';

export interface OutboundChannelDecision {
  channel: string;
  allowed: boolean;
  reason?: string;
}

export interface OutboundFilterResult {
  allowed: string[];
  blocked: OutboundChannelDecision[];
}

/**
 * Filtre une liste de canaux selon les consentements RÉELLEMENT accordés.
 * - `in_app` (et `app`) ne sont jamais bloqués.
 * - Pour les canaux sortants, si un utilisateur est fourni, on vérifie que la
 *   finalité est consentie (opt-in). Sans utilisateur (niveau organisation),
 *   on ne bloque pas, faute de consentement individuel à consulter.
 */
export async function filterOutboundByConsent(params: {
  userId?: number | null;
  purpose: string;
  channels: string[];
}): Promise<OutboundFilterResult> {
  const allowed: string[] = [];
  const blocked: OutboundChannelDecision[] = [];

  let map: Record<string, boolean> | null = null;
  if (params.userId) {
    try {
      map = await getConsentMap(params.userId);
    } catch {
      map = null;
    }
  }

  for (const channel of params.channels) {
    if (!isOutboundChannel(channel) || map === null) {
      allowed.push(channel);
      continue;
    }
    if (map[params.purpose]) {
      allowed.push(channel);
    } else {
      blocked.push({
        channel,
        allowed: false,
        reason: `consentement « ${consentPurposeMeta(params.purpose).label} » non accordé`,
      });
    }
  }

  return { allowed, blocked };
}

/** Vrai si l'on peut envoyer une communication sortante à cet utilisateur. */
export async function canSendOutbound(userId: number, purpose: string): Promise<boolean> {
  if (!userId) return false;
  return isConsented(userId, purpose);
}